import './styles.scss'

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useActions, useValues } from 'kea'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import NewGoodFit from 'lib/components/new-good-fit'
import Share from 'lib/icons/share'
import Twicpics from 'lib/tags/twicpics'
import railsContext from 'lib/selectors/rails-context'
import displayProfileName from 'lib/utils/display-profile-name'
import redirect from 'lib/utils/redirect-url'
import I18n from 'lib/i18n'

import VideoPlayer from '~/scenes/shared/new-video-player'

import FavoriteButton from '../favorite-button'
import ReviewLine from '../review-line'

import messageIcon from '~/scenes/course/show/_assets/message-icon.svg'
import verifiedBadgeIcon from '~/scenes/course/show/_assets/verified-icon.svg'

import courseLogic from '~/scenes/course/show/logic'

const MAX_CHARACTERS = 14
const LARGE_SCREEN_WIDTH = 1008

const ContactWidget = ({ isAboveClassTitleSectionVisible }) => {
  const previousVisibilityRef = useRef(isAboveClassTitleSectionVisible)
  const [bounceClass, setBounceClass] = useState('')
  const { openModal, enableSharing, enableGoodFitModal } = useActions(courseLogic)
  const { course, userData, video, avatar, windowWidth, needEmail, contactRedirect } = useValues(courseLogic)

  const { dpr } = useSelector(railsContext)

  const { messageUrl, messageUrlIsInbox } = course

  const { reviewCount, reviewSum, avgReview100, firstName, url, avatarAlt: userAvatarAlt, avatarUrl } = userData

  const reviewText = I18n.t('profiles.profile_layout.review_count', { count: reviewCount })

  const shortenedFirstName = displayProfileName(firstName, MAX_CHARACTERS)

  const isVideoListed = video && video.state === 'listed'

  const { avatarAlt: courseAvatarAlt } = course || {}

  const handleMessageClick = useCallback(() => {
    const shouldRedirectToMessageUrl = messageUrlIsInbox || windowWidth < LARGE_SCREEN_WIDTH

    if (needEmail) {
      redirect(contactRedirect)
    } else if (shouldRedirectToMessageUrl) {
      redirect(messageUrl)
    } else if (windowWidth >= LARGE_SCREEN_WIDTH) {
      openModal()
    }
  }, [messageUrlIsInbox, windowWidth, needEmail, contactRedirect, messageUrl, openModal])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const isRedirectedFromSignUp = urlParams.get('contact')

    if (isRedirectedFromSignUp) {
      openModal()

      urlParams.delete('contact')
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`
      window.history.replaceState({}, '', newUrl)
    }
  }, [openModal])

  useEffect(() => {
    const previousVisibility = previousVisibilityRef.current

    if (previousVisibility && !isAboveClassTitleSectionVisible) {
      setBounceClass(' bounce')

      const timeout = setTimeout(() => {
        setBounceClass('')
      }, 600)

      return () => clearTimeout(timeout)
    }

    previousVisibilityRef.current = isAboveClassTitleSectionVisible
  }, [isAboveClassTitleSectionVisible])

  return (
    <div className={`contact-widget${bounceClass}`}>
      {video && video.state === 'listed' ? (
        <div className='video-container'>
          <VideoPlayer isVisible={true} videoId={video.uid} title={shortenedFirstName} withGradient />
        </div>
      ) : (
        <>
          {!isAboveClassTitleSectionVisible && (
            <div
              className={`photo-name-verified-badge-container fade-wrapper ${
                !isAboveClassTitleSectionVisible ? 'fade-in' : 'fade-out'
              }`}
            >
              <div className={`profile-image-container ${!isAboveClassTitleSectionVisible ? 'visible' : ''}`}>
                <Twicpics
                  className='custom'
                  src={avatar || avatarUrl}
                  width='100%'
                  alt={courseAvatarAlt || userAvatarAlt}
                  dpr={dpr}
                />
                <div className='gradient-overlay' id='gradientOverlay' />
              </div>
            </div>
          )}
        </>
      )}
      <div
        className={`name-badge-reviews-share-favourite-container${
          isAboveClassTitleSectionVisible || isVideoListed ? ' is-class-title-section-visible' : ''
        }`}
      >
        {((!isAboveClassTitleSectionVisible && !isVideoListed) || reviewCount > 0) && (
          <div className='name-badge-reviews-container'>
            {!isAboveClassTitleSectionVisible && !isVideoListed && (
              <div className='name-verified-badge-container'>
                <a className='name mobile-name' href={url}>
                  {shortenedFirstName}
                </a>
                <div className='verified-badge-container'>
                  <img
                    className='verified-badge-icon'
                    alt='verified badge'
                    src={verifiedBadgeIcon}
                    width={19}
                    height={19}
                  />
                </div>
              </div>
            )}
            {reviewCount > 0 && (
              <ReviewLine
                avgReview100={avgReview100}
                reviewText={reviewText}
                reviewCount={reviewCount}
                reviewSum={reviewSum}
              />
            )}
          </div>
        )}
        <div className='share-favourite-container'>
          <div>
            <Share onClick={enableSharing} />
          </div>
          <div>{course && <FavoriteButton />}</div>
        </div>
      </div>
      <div className='contact-teacher-button' onClick={handleMessageClick}>
        <div className='contact-teacher-icon'>
          <img className='message-icon' alt='message icon' src={messageIcon} width={20} height={20} />
        </div>
        <div className='contact-teacher-text'>
          {I18n.t('courses.show_contact.button', { first_name: shortenedFirstName })}
        </div>
      </div>
      <NewGoodFit onClick={enableGoodFitModal} />
    </div>
  )
}

ContactWidget.propTypes = {
  isAboveClassTitleSectionVisible: PropTypes.bool
}

export default ContactWidget
